'use client';

import React, { useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, styled } from '@mui/material';
import { breakpoints, color, spacing } from '@/theme/tokens';
import IconChevronDown from '@/assets/icons/IconChevronDown';
import { renderTextWithLinks } from '@/helpers/renderTextWithLinks';

export interface FAQItem {
    question: string;
    answer: string;
}

export interface FAQSection {
    title: string;
    items: FAQItem[];
}

interface Props {
    pageTitle: string;
    sections: FAQSection[];
}

const StyledFAQSection = styled(Box)`
    margin-bottom: ${spacing[24]};
    padding-bottom: ${spacing[24]};
    border-bottom: 1px solid ${color.black[10]};
    @media (max-width: ${breakpoints.md}) {
        margin-bottom: ${spacing[20]};
        padding-bottom: ${spacing[20]};
    }
    @media (max-width: ${breakpoints.sm}) {
        margin-bottom: ${spacing[12]};
        padding-bottom: ${spacing[12]};
    }
    &:last-child {
        margin-bottom: 0;
        border-bottom: none;
        padding-bottom: 0;
    }
`;

// The questions and answers arrive as data (src/content/services/en/faq-data.json) rather than as
// MDX children. Until W8M-8239 this component rendered the MDX into a display:none box and scraped
// the headings back out of the DOM in an effect, so the server HTML carried no accordions at all
// and the visible text only existed after hydration — which also made a FAQPage rich result
// impossible, since Google requires the markup to match visible page content.
export default function FAQContent({ pageTitle, sections }: Props) {
    const [expanded, setExpanded] = useState<string[]>([]);

    const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(prev => (isExpanded ? [...prev, panel] : prev.filter(id => id !== panel)));
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: spacing[16], sm: spacing[24], md: spacing[32] },
                alignItems: 'start',
                width: '100%',
            }}
        >
            <Typography variant="heading2bold" component="h2" color={color.gray[80]}>
                {pageTitle}
            </Typography>

            <Box sx={{ width: '100%' }}>
                {sections.map((section, sectionIndex) => (
                    <StyledFAQSection key={`section-${sectionIndex}`}>
                        <Typography
                            variant="label5bold"
                            component="h3"
                            color={color.gray[60]}
                            sx={{
                                marginBottom: spacing[12],
                            }}
                        >
                            {section.title}
                        </Typography>

                        {section.items.map((item, itemIndex) => {
                            const panelId = `section-${sectionIndex}-item-${itemIndex}`;
                            // Short config/page-scoped list (tens of entries) — a Set here buys no measurable win.
                            // react-doctor-disable-next-line react-doctor/js-set-map-lookups
                            const isExpanded = expanded.includes(panelId);

                            return (
                                <Accordion
                                    key={panelId}
                                    slotProps={{ heading: { component: 'h4' } }}
                                    expanded={isExpanded}
                                    onChange={handleChange(panelId)}
                                    sx={{
                                        backgroundColor: color.gray[5],
                                        borderRadius: `${spacing[8]} !important`,
                                        boxShadow: 'none',
                                        marginBottom: spacing[8],
                                        marginTop: spacing[0],
                                        '&.Mui-expanded': {
                                            marginTop: 0,
                                            marginBottom: spacing[8],
                                        },
                                        '&:before': {
                                            display: 'none',
                                        },
                                        '&:last-child': {
                                            marginBottom: 0,
                                        },
                                        '&:has(.MuiAccordionSummary-root.Mui-focusVisible)': {
                                            outline: `2px solid ${color.gray[80]}`,
                                            outlineOffset: '2px',
                                        },
                                        '& .MuiAccordionSummary-root': {
                                            '&.Mui-focusVisible, &:focus, &:focus-visible': {
                                                outline: 'none',
                                            },
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        sx={{
                                            padding: `${spacing[16]} ${spacing[12]} ${spacing[16]} ${spacing[16]}`,
                                            '&.Mui-expanded': {
                                                padding: `${spacing[16]} ${spacing[12]} ${spacing[16]} ${spacing[16]}`,
                                                minHeight: 0,
                                            },
                                            '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                                                {
                                                    marginTop: 0,
                                                    marginBottom: 0,
                                                },
                                            '& .MuiAccordionSummary-content': {
                                                paddingRight: spacing[16],
                                            },
                                        }}
                                        expandIcon={<IconChevronDown color={color.gray[70]} width={24} height={24} />}
                                    >
                                        <Typography
                                            variant="label6bold"
                                            component="span"
                                            color={color.gray[80]}
                                            sx={{ flex: 1 }}
                                        >
                                            {item.question}
                                        </Typography>
                                    </AccordionSummary>

                                    <AccordionDetails
                                        sx={{
                                            padding: spacing[16],
                                            paddingTop: 0,
                                        }}
                                    >
                                        <Typography variant="paragraph4regular" component="p" color={color.gray[80]}>
                                            {renderTextWithLinks(item.answer)}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </StyledFAQSection>
                ))}
            </Box>
        </Box>
    );
}
